import { formatDateUTCToString } from '@/helpers/formatStringDate';
import { parseDateUtc } from '@/helpers/parseToUTC';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { safeDynamic } from '@/utils/safeDynamic';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

export interface DateRangePickFieldSeparatedProps {
  id?: string;
  label?: string | React.ReactNode;
  name: string;
  errors?: string | string[] | Record<string, any> | null; // Updated to Record<string, any> to allow indexing
  touched?: boolean;
  isRequired?: boolean;
  fontSize?: string | number;
  fontWeight?: string;
  italic?: boolean;
  isDisabled?: boolean;
  labelFontSize?: string;
  [x: string]: any;
}

const DatePicker = safeDynamic<any>(() => import('react-datepicker'), {
  ssr: false,
});

const areSameDates = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;

  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

function useDateRangePicker(value: DateRange | undefined, setFieldValue: (value: any) => void) {
  const [startDate, setStartDate] = useState<Date | null>(parseDateUtc(value?.startDate));
  const [endDate, setEndDate] = useState<Date | null>(parseDateUtc(value?.endDate));
  const [isSelectingEndDate, setIsSelectingEndDate] = useState<boolean>(false);

  const userInteractionRef = useRef(false);
  const preventEffectRef = useRef(false);

  useEffect(() => {
    if (preventEffectRef.current) {
      preventEffectRef.current = false;
      return;
    }

    if (!userInteractionRef.current) {
      const newStartDate = parseDateUtc(value?.startDate);
      const newEndDate = parseDateUtc(value?.endDate);

      if (!areSameDates(newStartDate, startDate)) {
        setStartDate(newStartDate);
      }

      if (!areSameDates(newEndDate, endDate)) {
        setEndDate(newEndDate);
      }
    }
  }, [value?.startDate, value?.endDate, startDate, endDate]);

  const handleDateChange = useCallback(
    (selectedDate: Date | null) => {
      if (!selectedDate) return;

      userInteractionRef.current = true;
      preventEffectRef.current = true;

      try {
        if (!isSelectingEndDate) {
          setStartDate(selectedDate);

          if (endDate && selectedDate > endDate) {
            setEndDate(null);
            setFieldValue({
              startDate: formatDateUTCToString(selectedDate, true),
              endDate: null,
            });
          } else {
            setFieldValue({
              ...value,
              startDate: formatDateUTCToString(selectedDate, true),
            });
          }
          setIsSelectingEndDate(true);
        } else {
          if (endDate && areSameDates(selectedDate, endDate)) {
            setEndDate(null);
            setFieldValue({
              ...value,
              endDate: null,
            });
          } else {
            setEndDate(selectedDate);
            setFieldValue({
              ...value,
              endDate: formatDateUTCToString(selectedDate, true),
            });
          }
          setIsSelectingEndDate(false);
        }
      } finally {
        userInteractionRef.current = false;
      }
    },
    [endDate, isSelectingEndDate, setFieldValue, value],
  );

  const clearEndDate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      userInteractionRef.current = true;
      preventEffectRef.current = true;

      setEndDate(null);
      setFieldValue({ ...value, endDate: null });

      userInteractionRef.current = false;
    },
    [setFieldValue, value],
  );

  return {
    startDate,
    endDate,
    isSelectingEndDate,
    setIsSelectingEndDate,
    handleDateChange,
    clearEndDate,
    userInteractionRef,
    preventEffectRef,
  };
}

export function DateRangePickFieldSeparated(props: DateRangePickFieldSeparatedProps) {
  const {
    id,
    label,
    mb,
    isRequired,
    errors,
    touched,
    fontSize,
    fontWeight,
    italic,
    setFieldValue,
    value,
    isDisabled,
    labelFontSize,
    setTouched,
    h,
    bg,
    borderColor,
    border,
    labelMb = 4,
    onDark,
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];
  const textColorPrimary = useColorModeValue(lightColor, darkColor);

  const responsiveFontSize = useBreakpointValue({
    base: fontSize || '16px',
    md: fontSize || '20px',
  });
  const dateFormat = useBreakpointValue({
    base: {
      day: 'numeric' as const,
      month: 'numeric' as const,
      year: '2-digit' as const,
    },
    md: {
      day: 'numeric' as const,
      month: 'long' as const,
      year: 'numeric' as const,
    },
  });

  const {
    startDate,
    endDate,
    isSelectingEndDate,
    setIsSelectingEndDate,
    handleDateChange,
    clearEndDate,
    userInteractionRef,
  } = useDateRangePicker(value, setFieldValue);

  const formatDisplayDate = useCallback(
    (date: Date | null) => {
      if (!date) return '';

      const options = dateFormat || {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      };

      const formatted = date.toLocaleDateString('en-GB', options);
      return options.year === '2-digit' ? formatted.replace(/\//g, '-') : formatted;
    },
    [dateFormat],
  );

  const startDateDisplay = useMemo(
    () => (startDate ? formatDisplayDate(startDate) : 'Start date'),
    [startDate, formatDisplayDate],
  );

  const endDateDisplay = useMemo(
    () => (endDate ? formatDisplayDate(endDate) : 'End date'),
    [endDate, formatDisplayDate],
  );

  const getErrorString = useCallback((): string | undefined => {
    if (!errors) return undefined;
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors)) return errors[0];
    if (typeof errors === 'object') {
      const errorKeys = Object.keys(errors as Record<string, any>);
      if (errorKeys.length > 0) {
        const firstError = (errors as Record<string, any>)[errorKeys[0]];
        return typeof firstError === 'string' ? firstError : undefined;
      }
    }
    return undefined;
  }, [errors]);

  const errorMessage = useMemo(() => getErrorString(), [getErrorString]);

  const CustomInput = useMemo(
    () =>
      forwardRef<HTMLDivElement, any>(({ onClick }, ref) => (
        <Flex
          ref={ref}
          w="100%"
          border={border || '3px solid'}
          borderColor={borderColor || (isDisabled ? 'gray' : 'white')}
          borderRadius="10px"
          bg={bg || 'blue.800'}
          h={h || '60px'}
          maxH="60px"
          onClick={isDisabled ? undefined : onClick}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          <Flex
            flex="1"
            align="center"
            justify="flex-start"
            p="0 15px"
            pl={{ base: '15px', md: '30px' }}
            pr={{ base: '15px', md: '30px' }}
            borderRight="2px solid"
            borderColor={borderColor || (isDisabled ? 'gray' : 'white')}
            color={isDisabled ? 'gray' : textColorPrimary}
            fontSize={responsiveFontSize}
            fontWeight={fontWeight || '700'}
            style={{ fontStyle: italic ? 'italic' : 'normal' }}
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectingEndDate(false);
              onClick();
            }}
            minWidth="50%"
            height="100%"
          >
            <Box as="span" whiteSpace="nowrap" minWidth="100%">
              {startDateDisplay}
            </Box>
          </Flex>

          <Flex
            flex="1"
            align="center"
            justify="space-between"
            p="0 15px"
            pl={{ base: '15px', md: '30px' }}
            pr={{ base: '15px', md: '30px' }}
            color={isDisabled ? 'gray' : textColorPrimary}
            fontSize={responsiveFontSize}
            fontWeight={fontWeight || '700'}
            style={{ fontStyle: italic ? 'italic' : 'normal' }}
            position="relative"
            minWidth="50%"
            height="100%"
          >
            <Box
              as="span"
              whiteSpace="nowrap"
              flex="1"
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                setIsSelectingEndDate(true);
                onClick();
              }}
            >
              {endDateDisplay}
            </Box>

            {endDate && !isDisabled && (
              <IconButton
                aria-label="Clear end date"
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                color={isDisabled ? 'gray' : textColorPrimary}
                onClick={clearEndDate}
                _hover={{ bg: 'blue.600' }}
                ml={2}
                minWidth="auto"
              />
            )}
          </Flex>
        </Flex>
      )),
    [
      border,
      borderColor,
      isDisabled,
      bg,
      h,
      responsiveFontSize,
      fontWeight,
      italic,
      startDateDisplay,
      endDateDisplay,
      setIsSelectingEndDate,
      endDate,
      clearEndDate,
    ],
  );

  return (
    <Flex direction="column" mb={mb || '30px'} width="full" pos="relative">
      <FormControl
        isRequired={isRequired}
        isInvalid={!!errorMessage && touched}
        isDisabled={isDisabled}
        width="100%"
      >
        {label && (
          <FormLabel
            display="flex"
            htmlFor={id}
            fontSize={labelFontSize || '2xl'}
            lineHeight="normal"
            color={textColorPrimary}
            fontWeight="bold"
            mb={labelMb}
            _hover={{ cursor: 'pointer' }}
          >
            {label}
          </FormLabel>
        )}

        <Box w="100%">
          <DatePicker
            selected={isSelectingEndDate ? endDate : startDate}
            onChange={handleDateChange}
            minDate={isSelectingEndDate && startDate ? startDate : undefined}
            disabled={isDisabled}
            onCalendarOpen={() => {
              setTouched && setTouched(true);
              userInteractionRef.current = true;
            }}
            customInput={<CustomInput />}
          />
        </Box>

        {errorMessage && touched && <FormErrorMessage ml="10px">{errorMessage}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
